import { z } from 'zod'

export const PURCHASE_FLOW_PARAM = 'purchase_flow'
export const PURCHASE_FLOW_SESSION_COOKIE = 'podo_purchase_session'
export const PURCHASE_FLOW_TTL_SECONDS = 2 * 60 * 60
export const purchaseFlowIdSchema = z.string().uuid()
export const purchaseCountrySchema = z.enum(['KR', 'JP'])
export type PurchaseCountry = z.infer<typeof purchaseCountrySchema>
export type PurchaseSearchParams = Record<string, string | string[] | null | undefined>

const UNSAFE_TEXT = /[\u0000-\u0020\u007f\\]/
const UNSAFE_ENCODING = /%(?:0[0-9a-f]|1[0-9a-f]|20|7f|5c|2f|25)/i
const ENTITY_ID = /^[a-zA-Z0-9_-]{1,128}$/
const RETURN_QUERY_DENY =
  /^(?:return(?:to|url)?|redirect(?:uri|url|to)?|destination|url|next|continue|access_?token|refresh_?token|token|sid|code|purchase_flow)$/i
const PURCHASE_PATH =
  /^(?:\/subscribes\/(?:jp\/)?tickets|\/subscribes\/payment\/[a-zA-Z0-9_-]{1,128}|\/products\/payment\/[a-zA-Z0-9_-]{1,128}|\/jp\/checkout)$/

function hasDuplicateParams(params: URLSearchParams) {
  return new Set(params.keys()).size !== [...params.keys()].length
}

export function parsePurchaseTarget(value: unknown) {
  if (typeof value !== 'string' || value.length > 4096 || !value.startsWith('/') || value.startsWith('//')) return null
  if (UNSAFE_TEXT.test(value) || UNSAFE_ENCODING.test(value.split('?')[0])) return null
  const url = new URL(value, 'https://purchase.invalid')
  if (
    url.origin !== 'https://purchase.invalid' ||
    !PURCHASE_PATH.test(url.pathname) ||
    url.hash ||
    /\/(?:return|success)$/.test(url.pathname)
  )
    return null
  if (hasDuplicateParams(url.searchParams)) return null
  if (url.pathname === '/jp/checkout' && !ENTITY_ID.test(url.searchParams.get('sku') ?? '')) return null
  for (const key of url.searchParams.keys()) {
    if (/^(?:access_?token|refresh_?token|token|sid)$/i.test(key)) return null
  }
  // Caller input never determines allocation. Only the server-created record does.
  for (const key of [PURCHASE_FLOW_PARAM, 'flow_on', 'enabled', 'flag', 'group']) url.searchParams.delete(key)
  return {
    target: `${url.pathname}${url.search}`,
    flowType: (url.pathname.endsWith('/tickets') ? 'B' : 'C') as 'B' | 'C',
  }
}

export function validatePurchaseReturnTo(value: unknown, allowedOrigins: readonly string[]): string | null {
  if (typeof value !== 'string' || value.length > 2048 || UNSAFE_TEXT.test(value) || !/^https?:\/\//.test(value))
    return null
  try {
    const url = new URL(value)
    if (!allowedOrigins.includes(url.origin) || url.username || url.password || UNSAFE_ENCODING.test(url.pathname))
      return null
    // Marketing pages are CMS slugs, not authentication/payment/API execution surfaces.
    if (!/^\/(?:[a-zA-Z0-9_-]+\/?)*$/.test(url.pathname)) return null
    if (
      url.pathname
        .split('/')
        .some((part) =>
          /^(?:api|\.api|auth|login|callback|checkout|payment|purchase|redirect|open-in-app)$/i.test(part),
        )
    )
      return null
    if (hasDuplicateParams(url.searchParams)) return null
    for (const [key, value] of url.searchParams) {
      if (
        RETURN_QUERY_DENY.test(key) ||
        UNSAFE_TEXT.test(key) ||
        UNSAFE_TEXT.test(value) ||
        /%[0-9a-f]{2}/i.test(key + value) ||
        /(?:https?:|javascript:|\/\/)/i.test(value)
      )
        return null
    }
    return url.toString()
  } catch {
    return null
  }
}

export function withPurchaseFlow(target: string, flowId: string | null) {
  const url = new URL(target, 'https://purchase.invalid')
  if (flowId) url.searchParams.set(PURCHASE_FLOW_PARAM, purchaseFlowIdSchema.parse(flowId))
  return `${url.pathname}${url.search}${url.hash}`
}

export function buildPurchaseBackUrl(
  legacyBackUrl: string,
  params: PurchaseSearchParams,
  flow: PurchaseFlowContextValue,
) {
  if (!flow.enabled) return withPurchaseFlow(legacyBackUrl, flow.flowId)
  if (flow.flowType === 'C' && flow.returnTo) return flow.returnTo
  const url = new URL(legacyBackUrl, 'https://purchase.invalid')
  for (const key of [
    'langType',
    'lang',
    'product',
    'period',
    'source',
    'languageGroup',
    'preset_applied',
    'preset_product',
  ]) {
    const value = params[key]
    if (typeof value === 'string' && value.length <= 128) url.searchParams.set(key, value)
  }
  return withPurchaseFlow(`${url.pathname}${url.search}`, flow.flowId)
}

export function purchaseAuthUrl(flowId: string, country?: PurchaseCountry, countrySelected = false) {
  const params = new URLSearchParams({
    destination: 'PURCHASE_FLOW',
    purchase_flow: purchaseFlowIdSchema.parse(flowId),
  })
  if (country) params.set('country', country)
  if (countrySelected) params.set('countrySelected', 'true')
  return `/login?${params}`
}

export function purchaseResumeUrl(flowId: string) {
  return `/purchase/resume?${new URLSearchParams({ purchase_flow: purchaseFlowIdSchema.parse(flowId) })}`
}

export function purchaseTicketsUrl(country: PurchaseCountry, flowId: string | null, language?: string | null) {
  const url = new URL(country === 'JP' ? '/subscribes/jp/tickets' : '/subscribes/tickets', 'https://purchase.invalid')
  const supportedLanguages = country === 'JP' ? ['KR', 'ENKR', 'EN'] : ['EN', 'JP']
  url.searchParams.set('preset', 'best')
  url.searchParams.set('source', 'marketing')
  url.searchParams.set(
    'langType',
    language && supportedLanguages.includes(language) ? language : country === 'JP' ? 'KR' : 'JP',
  )
  return withPurchaseFlow(`${url.pathname}${url.search}`, flowId)
}

export const purchaseFlowRecordSchema = z.object({
  version: z.literal(1),
  id: purchaseFlowIdSchema,
  sessionId: purchaseFlowIdSchema,
  userId: z.string().min(1).max(128).nullable(),
  initialEnabled: z.boolean(),
  assignmentSubject: z.string().min(1).max(128),
  country: purchaseCountrySchema,
  selectedCountry: purchaseCountrySchema.nullable(),
  flowType: z.enum(['B', 'C']),
  target: z.string().max(4096),
  returnTo: z.string().max(2048).nullable(),
  createdAt: z.number(),
  expiresAt: z.number(),
})
export type PurchaseFlowRecord = z.infer<typeof purchaseFlowRecordSchema>

export function ownsPurchaseFlow(
  record: PurchaseFlowRecord,
  sessionId: string | undefined,
  userId: string | null,
  now = Date.now(),
) {
  return record.sessionId === sessionId && record.expiresAt > now && (!record.userId || record.userId === userId)
}

export interface PurchaseFlagDecision {
  enabled: boolean
  globallyDisabled: boolean
}
export function effectivePurchaseFlag(decision: PurchaseFlagDecision, record: PurchaseFlowRecord | null) {
  return !decision.globallyDisabled && (record?.initialEnabled ?? decision.enabled)
}

export interface PurchaseFlowContextValue {
  enabled: boolean
  initialEnabled?: boolean
  flowId: string | null
  flowType: 'B' | 'C' | null
  returnTo: string | null
}
